import React from 'react';
import { motion } from 'framer-motion';
import { ContactForm } from '../components/sections/ContactForm';
import { Card, CardContent } from '../components/ui/card';

const contactInfo = [
  {
    icon: 'fa-phone',
    title: 'Phone',
    details: ['+27 75 278 1104'],
    action: 'tel:+27752781104'
  },
  {
    icon: 'fa-whatsapp',
    title: 'WhatsApp',
    details: ['+27 75 278 1104'],
    action: 'https://wa.me/27752781104',
    external: true
  },
  {
    icon: 'fa-envelope',
    title: 'Email',
    details: ['info@lastupholstery.co.za'],
    action: 'mailto:info@lastupholstery.co.za'
  },
  {
    icon: 'fa-map-marker-alt',
    title: 'Location',
    details: ['Gauteng, South Africa'],
    action: null
  }
];

const businessHours = [
  { day: 'Monday - Friday', hours: '8:00 AM - 5:00 PM' },
  { day: 'Saturday', hours: '9:00 AM - 2:00 PM' },
  { day: 'Sunday', hours: 'Closed' }
];

export const ContactPage = () => {
  return (
    <main data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&q=80"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-leather/80"></div>
        </div>
        <div className="relative text-center z-10 px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-gold font-body text-sm uppercase tracking-widest"
          >
            Get In Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4"
          >
            Contact Us
          </motion.h1>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-cream">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="font-heading text-2xl md:text-3xl text-leather mb-2">
                  Send Us a Message
                </h2>
                <p className="text-leather/60 font-body mb-8">
                  Have questions or need assistance? Fill out the form below and we'll get back to you as soon as possible.
                </p>
                <ContactForm />
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Info Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl text-leather mb-6">
                      Contact Information
                    </h3>
                    <div className="space-y-6">
                      {contactInfo.map((info) => (
                        <div key={info.title} className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                            <i className={`fas ${info.icon} text-gold text-lg`}></i>
                          </div>
                          <div>
                            <p className="font-heading font-semibold text-leather mb-1">
                              {info.title}
                            </p>
                            {info.details.map((detail, i) => (
                              info.action ? (
                                <a
                                  key={i}
                                  href={info.action}
                                  target={info.external ? '_blank' : undefined}
                                  rel={info.external ? 'noopener noreferrer' : undefined}
                                  className="text-leather/60 font-body text-sm hover:text-gold transition-colors block"
                                >
                                  {detail}
                                </a>
                              ) : (
                                <p key={i} className="text-leather/60 font-body text-sm">
                                  {detail}
                                </p>
                              )
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Business Hours */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="border-0 shadow-card">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl text-leather mb-4">
                      Business Hours
                    </h3>
                    <div className="space-y-3">
                      {businessHours.map((schedule) => (
                        <div key={schedule.day} className="flex justify-between text-sm">
                          <span className="text-leather/70 font-body">{schedule.day}</span>
                          <span className="text-leather font-body font-medium">{schedule.hours}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Quick WhatsApp */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <a 
                  href="https://wa.me/27752781104"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="border-0 shadow-card bg-[#25D366] hover:bg-[#20bd5a] transition-colors cursor-pointer">
                    <CardContent className="p-6 text-center">
                      <i className="fab fa-whatsapp text-white text-4xl mb-3"></i>
                      <h3 className="font-heading text-xl text-white mb-1">
                        Chat on WhatsApp
                      </h3>
                      <p className="text-white/80 font-body text-sm">
                        Quick response guaranteed
                      </p>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
